// Monthly billing rules shared by the month-end run, the invoice editor and the
// checks: pro-rata for projects that start or end mid-month, and "does this line
// look right?" warnings compared with the project price and the last invoice.

import { daysBetween, fmtMoney, lastDayOfMonth, num, projectLine, round2, uid } from "./utils";

const shortDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });

// basis: "30" (price ÷ 30 × days, the usual agency rule), "actual" (÷ days in the month) or "off"
export function prorateFor(project, ym, basis = "30") {
  if (!project || !ym || project.billing !== "monthly" || basis === "off") return null;
  const first = `${ym}-01`;
  const last = lastDayOfMonth(ym);
  const dim = daysBetween(first, last) + 1;
  const start = project.startDate || "";
  const end = project.endDate || "";
  if (start && start > last) return { factor: 0, days: 0, of: basis === "actual" ? dim : 30, reason: "starts later" };
  if (end && end < first) return { factor: 0, days: 0, of: basis === "actual" ? dim : 30, reason: "ended" };
  const from = start && start > first ? start : first;
  const to = end && end < last ? end : last;
  const active = daysBetween(from, to) + 1;
  if (active >= dim) return null; // full month
  const of = basis === "actual" ? dim : 30;
  const days = Math.min(active, of);
  if (days >= of) return null;
  return { factor: days / of, days, of, from, to, note: `pro-rata ${days}/${of} days, ${shortDate(from)}–${shortDate(to)}` };
}

// Invoice line for a project in a given month (pro-rated when needed). Returns null
// when the project isn't running at all that month.
export function projectToLine(p, ym, { basis = "30", taxRate } = {}) {
  const pr = prorateFor(p, ym, basis);
  if (pr && pr.factor === 0) return null;
  const qty = num(p.qty) || 1;
  const full = num(p.rate);
  const rate = pr ? round2(full * pr.factor) : full;
  return {
    id: uid(),
    projectId: p.id,
    description: projectLine(p) + (pr ? ` (${pr.note})` : ""),
    hsn: p.hsn || "",
    tag: p.tag || "",
    qty,
    rate,
    taxRate: taxRate ?? num(p.taxRate),
    ...(pr ? { prorated: { days: pr.days, of: pr.of, fullRate: full } } : {}),
  };
}

const monthOf = (inv) => inv.periodMonth || (inv.date || "").slice(0, 7);

// Warnings for an invoice: lines whose amount differs from the project price
// (after pro-rata) or from what was billed for the same project last time.
export function invoiceChecks(inv, data, { basis = "30" } = {}) {
  if (!inv || (inv.docType || "invoice") !== "invoice") return [];
  const out = [];
  const ym = monthOf(inv);
  const cur = inv.currency;
  const m = (n) => fmtMoney(n, cur);
  const history = (data.invoices || [])
    .filter((x) => x.id !== inv.id && x.status !== "cancelled" && (x.docType || "invoice") === "invoice" && x.clientId === inv.clientId && (x.date || "") <= (inv.date || "9999"))
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  for (const l of inv.items || []) {
    const p = l.projectId && (data.projects || []).find((x) => x.id === l.projectId);
    if (!p || p.billing !== "monthly") continue;
    const amount = round2(num(l.qty) * num(l.rate));
    const full = round2(num(p.rate) * (num(p.qty) || 1));
    const pr = prorateFor(p, ym, basis);
    const expected = pr ? round2(full * pr.factor) : full;
    const name = p.name || l.description;
    if (pr && pr.factor === 0) {
      out.push({ lineId: l.id, level: "warn", text: `${name}: ${pr.reason === "ended" ? "the project ended before" : "the project starts after"} this billing month, but it’s on the invoice.` });
      continue;
    }
    if (Math.abs(amount - expected) > 0.009) {
      out.push({
        lineId: l.id,
        level: "warn",
        text: `${name}: ${m(amount)} on this invoice, but the monthly price is ${m(full)}${pr ? ` (${pr.note} → ${m(expected)})` : ""}.`,
        fix: { rate: round2(expected / (num(l.qty) || 1)) },
      });
    }
    // compare with the last time this project was billed
    for (const h of history) {
      const hl = (h.items || []).find((x) => x.projectId === p.id);
      if (!hl) continue;
      const prev = round2(num(hl.qty) * num(hl.rate));
      if (Math.abs(prev - amount) > 0.009) {
        const wasPartial = hl.prorated || /pro-rata/i.test(hl.description || "") || prev < full - 0.009;
        if (wasPartial && Math.abs(amount - full) < 0.01)
          out.push({ lineId: l.id, level: "info", text: `${name}: last invoice (${h.number}) was a part month at ${m(prev)}; this month is the full ${m(full)}.` });
        else
          out.push({ lineId: l.id, level: "warn", text: `${name}: ${m(prev)} on the last invoice (${h.number}) but ${m(amount)} now – check this is intended.` });
      }
      break;
    }
  }
  return out;
}

export const PRORATE_OPTIONS = [
  { value: "30", label: "Pro-rata on 30 days (price ÷ 30 × days)" },
  { value: "actual", label: "Pro-rata on actual days in the month" },
  { value: "off", label: "Always charge the full month" },
];

